import React, { useRef, useState, useCallback, useEffect } from "react";
import { useCanvas } from "../../../hooks/useCanvas";
import { useKeyboardShortcuts } from "../../../hooks/useKeyboardShortcuts";
import { getGlobalCreateAiSprite } from "../../../stores/globalStore";
import ContextMenu from "../../ContextMenu/ContextMenu";

const CanvasArea = ({
  handleCanvasDrop,
  handleDragOver,
  createVideoSprite,
  createImageSprite,
  createAudioSprite,
  createGifSprite,
  onReplaceAction,
  addComponentToCanvas
}) => {
  const containerRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    targetElement: null,
  });

  // Use the integrated canvas store with PIXI
  const {
    canvasElements,
    selectedElement,
    moveSelectedElements,
    canvasSize,
    selectElement,
    deleteElement,
    undo,
    redo,
    canUndo,
    canRedo,
    // PIXI-related state and actions
    pixiApp,
    viewport,
    pixiInitialized,
    centerAndFitViewport,
    zoomIn,
    zoomOut,
    copyElements,
    getClipboard,
    pasteElements,
  } = useCanvas(containerRef);

  useEffect(() => {
    if (!containerRef.current || !pixiInitialized || !viewport) return;

    let rafId = null;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width === 0 || height === 0) return;
        // Resize the PIXI renderer to match the new container size
        if (pixiApp?.renderer) {
          pixiApp.renderer.resize(width, height);
        }
        // Resize viewport screen dimensions
        viewport.resize(width, height);
        // Refit and recenter after a tick to ensure layout stability
        // Cancel previous RAF to avoid redundant calls during rapid resizing
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          centerAndFitViewport(60);
          rafId = null;
        });
      }
    });

    resizeObserver.observe(containerRef.current);

    // Initial fit trigger after init (extra safety net)
    requestAnimationFrame(() => {
      centerAndFitViewport(60);
    });

    return () => {
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [pixiInitialized, viewport, pixiApp, centerAndFitViewport, containerRef]);

  // NOTE: useCanvasElements is now called in CanvasPage to avoid duplication
  // We don't need drag and drop or sprite creators here since they're handled elsewhere

  // Hide context menu when clicking elsewhere
  const hideContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  // Handle right-click context menu
  const handleContextMenu = useCallback(
    (event) => {
      event.preventDefault();

      // Only show context menu if there's a selected element
      if (selectedElement) {
        const rect = canvasAreaRef.current.getBoundingClientRect();
        setContextMenu({
          visible: true,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          targetElement: selectedElement,
        });
      } else {
        hideContextMenu();
      }
    },
    [selectedElement, hideContextMenu]
  );

  const handleSpriteMove = useCallback(
    (event, shortcutName, shortcutConfig) => {
      // Check if an element is selected and if the shortcut has a payload
      if (selectedElement && shortcutConfig.payload) {
        const { x, y } = shortcutConfig.payload;
        moveSelectedElements(x, y);
      }
    },
    [selectedElement, moveSelectedElements]
  );

  // Shared paste handler used by both keyboard shortcut and context menu
  const handlePaste = useCallback(() => {
    pasteElements(addComponentToCanvas);
  }, [pasteElements, addComponentToCanvas]);

  // Define shortcut callbacks
  const shortcutCallbacks = {
    ZOOM_IN: useCallback(() => {
      zoomIn(viewport);
    }, [viewport]),

    ZOOM_OUT: useCallback(() => {
      zoomOut(viewport);
    }, [viewport]),

    ZOOM_FIT: useCallback(() => {
      centerAndFitViewport(60);
    }, [centerAndFitViewport]),

    ESCAPE: useCallback(() => {
      if (selectedElement) {
        selectElement(null);
      }
      hideContextMenu(); // Also hide context menu on escape
    }, [selectedElement, selectElement, hideContextMenu]),

    // Undo/Redo shortcuts
    UNDO: useCallback(() => {
      if (canUndo()) {
        console.log("⏪ Undoing last action");
        // Pass all sprite creators as an object
        undo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [
      canUndo,
      undo,
      createVideoSprite,
      createImageSprite,
      createAudioSprite,
      createGifSprite,
    ]),

    REDO: useCallback(() => {
      if (canRedo()) {
        console.log("⏩ Redoing action");
        // Pass all sprite creators as an object
        redo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [
      canRedo,
      redo,
      createVideoSprite,
      createImageSprite,
      createAudioSprite,
      createGifSprite,
    ]),

    REDO_ALT: useCallback(() => {
      if (canRedo()) {
        console.log("⏩ Redoing action (Alt)");
        // Pass all sprite creators as an object
        redo({
          createVideoSprite,
          createImageSprite,
          createAudioSprite,
          createGifSprite,
          createAiSprite: getGlobalCreateAiSprite(),
        });
      }
    }, [
      canRedo,
      redo,
      createVideoSprite,
      createImageSprite,
      createAudioSprite,
      createGifSprite,
    ]),

    // Delete shortcuts
    DELETE: useCallback(() => {
      if (selectedElement) {
        console.log("🗑️ Deleting selected element");
        deleteElement(selectedElement.id);
        selectElement(null);
      }
    }, [selectedElement, deleteElement, selectElement]),

    DELETE_ALT: useCallback(() => {
      if (selectedElement) {
        console.log("🗑️ Deleting selected element (Alt)");
        deleteElement(selectedElement.id);
        selectElement(null);
      }
    }, [selectedElement, deleteElement, selectElement]),

    MOVE_UP: handleSpriteMove,
    MOVE_DOWN: handleSpriteMove,
    MOVE_LEFT: handleSpriteMove,
    MOVE_RIGHT: handleSpriteMove,
    MOVE_UP_FAST: handleSpriteMove,
    MOVE_DOWN_FAST: handleSpriteMove,
    MOVE_LEFT_FAST: handleSpriteMove,
    MOVE_RIGHT_FAST: handleSpriteMove,

    // Copy/Paste shortcuts
    COPY: useCallback(() => {
      if (selectedElement) {
        copyElements();
      }
    }, [selectedElement, copyElements]),

    PASTE: useCallback(() => {
      handlePaste();
    }, [handlePaste]),
  };

  // Focus handlers
  const handleFocus = useCallback(() => {
    console.log("🎯 Canvas focused - shortcuts enabled");
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    console.log("🔄 Canvas blurred - shortcuts disabled");
    setIsFocused(false);
  }, []);

  // useEffect(() => {
  //   if (selectedElement && canvasAreaRef.current) {
  //     // Defer the focus call to the end of the execution queue.
  //     // This ensures it runs *after* PIXI has finished rendering the transform controls.
  //     const focusTimeout = setTimeout(() => {
  //       console.log("Element selected, focusing canvas area...");
  //       canvasAreaRef.current.focus();
  //     }, 0);

  //     // Cleanup the timeout if the component unmounts or selection changes again quickly
  //     return () => clearTimeout(focusTimeout);
  //   }
  // }, [selectedElement]);

  const handleClick = useCallback(
    (event) => {
      // Hide context menu on any click
      hideContextMenu();

      // Focus the canvas area when clicked
      if (canvasAreaRef.current) {
        canvasAreaRef.current.focus();
      }
    },
    [hideContextMenu]
  );

  // Use the keyboard shortcuts hook
  // const { isShortcutActive } = useKeyboardShortcuts(
  //   undefined,
  //   shortcutCallbacks,
  //   {
  //     targetRef: canvasAreaRef,
  //   }
  // );

  useKeyboardShortcuts(undefined, shortcutCallbacks, {
    targetRef: canvasAreaRef,
  });

  // Determine cursor based on current state
  // const getCursor = () => {
  //   if (isShortcutActive("PAN_MODE")) return "grab";
  //   return "default";
  // };

  const getCursor = () => {
    // This function is needed for the style property
    return "default";
  };

  return (
    <div
      ref={canvasAreaRef}
      onContextMenu={handleContextMenu}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
      onDrop={handleCanvasDrop}
      onDragOver={handleDragOver}
      style={{
        width: "100%",
        height: "100%",
        cursor: getCursor(),
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          style={{
            position: "absolute",
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000,
            transform: "translateY(-100%)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <ContextMenu
            targetElement={contextMenu.targetElement}
            onClose={hideContextMenu}
            onPaste={handlePaste}
            context="canvas"
            onCustomAction={(action, element) => {
              if (action === 'replace_media' && onReplaceAction) {
                onReplaceAction(element);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CanvasArea;
